// Marginal-return math for the scaling saturation curve. Copied (and adapted) from
// BudgetBuilder/budgetBuilderMath.ts — the only change is the field name: a
// ScalingCurvePoint carries its predicted outcome as `fit` where a BudgetBuilder
// CurvePoint uses `volume`. Kept curve-agnostic so revenue → marginal ROAS and
// volume → incremental units per dollar both fall out of the same slope.
//
// Why marginal (not average): under diminishing returns the average outcome/$ at a
// spend level (fit / spend) overstates what the *next* dollar returns. The slope of
// the fitted curve is the decision-relevant read for "should we scale this?", and
// break-even is where that slope falls through 1 (marginal ROAS = 1x).

import { ScalingCurveParams, ScalingCurvePoint } from './scalingData';

const usableParams = (params?: ScalingCurveParams): params is ScalingCurveParams =>
  params !== undefined && Number.isFinite(params.a) && Number.isFinite(params.b);

/**
 * Marginal outcome per dollar at index `i` — the fitted curve's slope. For a
 * revenue curve this is marginal ROAS ("what the next $1 returns"); for a volume
 * curve it's incremental units per dollar. With the fit's `params` (outcome =
 * a × spend^b) the slope is exact: b × fit / spend. Without them (older runs) it
 * falls back to a centered finite difference, one-sided at the endpoints
 * (clamped index), matching budgetBuilderMath. null at a non-finite fit or a
 * zero/negative spend (step).
 */
export const marginalPerDollar = (
  curve: ScalingCurvePoint[],
  i: number,
  params?: ScalingCurveParams
): number | null => {
  if (curve.length === 0) {
    return null;
  }
  const idx = Math.max(0, Math.min(curve.length - 1, i));
  if (usableParams(params)) {
    const { spend, fit } = curve[idx];
    return spend > 0 && Number.isFinite(fit) ? (params.b * fit) / spend : null;
  }
  const a = curve[Math.max(0, idx - 1)];
  const b = curve[Math.min(curve.length - 1, idx + 1)];
  if (!Number.isFinite(a.fit) || !Number.isFinite(b.fit)) {
    return null;
  }
  const dSpend = b.spend - a.spend;
  return dSpend > 0 ? (b.fit - a.fit) / dSpend : null;
};

// The curve's own scale: a = fit / spend^b at its first positive point. Taken
// from the plotted curve rather than params.a, so the break-even can't drift
// from the curve if the fit's `a` was in other units (e.g. daily spend).
const curveScale = (curve: ScalingCurvePoint[], b: number): number | null => {
  const point = curve.find(p => p.spend > 0 && Number.isFinite(p.fit) && p.fit > 0);
  return point ? point.fit / point.spend ** b : null;
};

/**
 * Break-even spend: where marginal ROAS falls through 1 — past it the next
 * dollar returns less than a dollar. With `params` it's exact: a·b·s^(b−1) = 1
 * gives s = (1 / (a·b))^(1 / (b−1)), for a diminishing curve (0 < b < 1), with
 * `a` read off the curve (curveScale).
 * Without them, the first curve point where the finite-difference slope drops
 * below 1. Either way null when that point isn't within the modeled spend range
 * (e.g. a combo that stays profitable at the margin across it). Only meaningful
 * for a revenue curve; a volume curve has no 1x break-even.
 */
export const breakevenSpend = (
  curve: ScalingCurvePoint[],
  params?: ScalingCurveParams
): number | null => {
  if (usableParams(params)) {
    const { b } = params;
    const a = curveScale(curve, b);
    if (curve.length === 0 || a === null || !(a > 0) || !(b > 0 && b < 1)) {
      return null;
    }
    const spend = (1 / (a * b)) ** (1 / (b - 1));
    const inRange = spend >= curve[0].spend && spend <= curve[curve.length - 1].spend;
    return Number.isFinite(spend) && inRange ? spend : null;
  }
  for (let i = 1; i < curve.length; i += 1) {
    const prev = marginalPerDollar(curve, i - 1);
    const curr = marginalPerDollar(curve, i);
    if (prev != null && curr != null && prev >= 1 && curr < 1) {
      return curve[i].spend;
    }
  }
  return null;
};

/**
 * Index of the last observed (historical) curve point — "current max spend", the
 * anchor for the current marginal ROAS read. Falls back to the last point if the
 * curve carries no historical rows.
 */
export const lastHistoricalIndex = (curve: ScalingCurvePoint[]): number => {
  for (let i = curve.length - 1; i >= 0; i -= 1) {
    if (curve[i].extrapolated === 'historical') {
      return i;
    }
  }
  return curve.length - 1;
};
